import React, { FC, useMemo, useState } from 'react'
import { RunningHourCounterDto, RunningHourReadingDto } from '../../core/pms/_models'
import { createRunningHourReading, estimateRunningHourReadingImpact } from '../../core/pms/_requests'
import { toast } from 'react-toastify'

interface Props {
  visible: boolean
  onClose: () => void
  counter: RunningHourCounterDto
  onSuccess: () => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const RunningHoursModal: FC<Props> = ({ visible, onClose, counter, onSuccess }) => {
  const todayStr = new Date().toISOString().split('T')[0]
  const [formData, setFormData] = useState<{
    readingDate: string
    value: string
  }>({
    readingDate: new Date().toISOString().split('T')[0],
    value: counter.currentValue?.toString() || '0',
  })
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [impactCount, setImpactCount] = useState<number | null>(null)
  const [pendingDto, setPendingDto] = useState<RunningHourReadingDto | null>(null)
  const [impactLoading, setImpactLoading] = useState(false)

  const isPastDate = useMemo(() => {
    if (!counter.lastReadingDate || !formData.readingDate) return false
    const selected = new Date(formData.readingDate)
    const lastReading = new Date(counter.lastReadingDate)
    selected.setHours(0, 0, 0, 0)
    lastReading.setHours(0, 0, 0, 0)
    return selected < lastReading
  }, [counter.lastReadingDate, formData.readingDate])

  const daysInPast = useMemo(() => {
    if (!isPastDate || !counter.lastReadingDate || !formData.readingDate) return 0
    const selected = new Date(formData.readingDate)
    const lastReading = new Date(counter.lastReadingDate)
    selected.setHours(0, 0, 0, 0)
    lastReading.setHours(0, 0, 0, 0)
    return Math.max(0, Math.floor((lastReading.getTime() - selected.getTime()) / (1000 * 60 * 60 * 24)))
  }, [isPastDate, counter.lastReadingDate, formData.readingDate])

  const { minVal, maxVal, helperText } = useMemo(() => {
    if (isPastDate) {
      const maxAllowed = counter.currentValue ?? 0
      const maxDecrease = daysInPast * 25
      const minAllowed = Math.max(0, maxAllowed - maxDecrease)
      const lastDateLabel = counter.lastReadingDate
        ? new Date(counter.lastReadingDate).toLocaleDateString()
        : 'latest date'
      return {
        minVal: minAllowed,
        maxVal: maxAllowed,
        helperText: `Past date reading (${daysInPast} day${daysInPast === 1 ? '' : 's'} before ${lastDateLabel}): Allowable range is ${minAllowed.toLocaleString()} – ${maxAllowed.toLocaleString()} hrs (allowing up to -25 hrs/day reduction).`,
      }
    } else {
      const effectiveMin = Math.max(counter.currentValue ?? 0, counter.minRequiredValue ?? 0)
      let text = `New reading must be ≥ ${effectiveMin.toLocaleString()} hrs`
      if (counter.minRequiredValue && counter.minRequiredValue > (counter.currentValue ?? 0)) {
        text += ` (floor set by last job done at ${counter.minRequiredValue.toLocaleString()} hrs)`
      }
      if (counter.currentValue > 0 && counter.lastReadingDate) {
        const lastReadingDate = new Date(counter.lastReadingDate)
        const selectedDate = new Date(formData.readingDate)
        selectedDate.setHours(0, 0, 0, 0)
        lastReadingDate.setHours(0, 0, 0, 0)
        let daysElapsed = Math.floor(
          (selectedDate.getTime() - lastReadingDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysElapsed < 0) daysElapsed = 0
        const maxIncrease = daysElapsed * 25
        if (daysElapsed > 0) {
          text += ` | Max increase: ${maxIncrease.toLocaleString()} hrs (${daysElapsed}d × 25 hrs/day)`
        }
      }
      return {
        minVal: effectiveMin,
        maxVal: undefined,
        helperText: text,
      }
    }
  }, [isPastDate, daysInPast, counter.currentValue, counter.minRequiredValue, counter.lastReadingDate, formData.readingDate])

  if (!visible) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { [key: string]: string } = {}

    if (!formData.readingDate) {
      newErrors.readingDate = 'Reading date is required'
    } else {
      const selected = new Date(formData.readingDate)
      const today = new Date(todayStr)
      selected.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      if (selected > today) {
        newErrors.readingDate = 'Future dates are not allowed'
      }
    }

    const newValue = Number(formData.value)
    if (!formData.value || isNaN(newValue)) {
      newErrors.value = 'Reading value is required'
    } else if (isPastDate) {
      if (maxVal != null && newValue > maxVal) {
        newErrors.value = `Reading value (${newValue.toLocaleString()} hrs) on past date cannot exceed the latest reading (${maxVal.toLocaleString()} hrs).`
      } else if (minVal != null && newValue < minVal) {
        newErrors.value = `Reading value (${newValue.toLocaleString()} hrs) is too low for past date. Maximum allowed decrease is ${(daysInPast * 25).toLocaleString()} hrs (${daysInPast}d × 25 hrs/day). Minimum allowable value is ${minVal.toLocaleString()} hrs.`
      }
    } else {
      const effectiveMin = Math.max(
        counter.currentValue ?? 0,
        counter.minRequiredValue ?? 0
      )
      if (newValue < effectiveMin) {
        newErrors.value = `Reading value must be ≥ ${effectiveMin.toLocaleString()} hrs (current running hours floor)`
      } else if (counter.currentValue > 0 && counter.lastReadingDate) {
        const lastReadingDate = new Date(counter.lastReadingDate)
        const selectedDate = new Date(formData.readingDate)
        selectedDate.setHours(0, 0, 0, 0)
        lastReadingDate.setHours(0, 0, 0, 0)

        let daysElapsed = Math.floor((selectedDate.getTime() - lastReadingDate.getTime()) / (1000 * 60 * 60 * 24))
        if (daysElapsed < 0) daysElapsed = 0
        const maxIncrease = daysElapsed * 25

        const increase = newValue - counter.currentValue
        if (increase > maxIncrease) {
          newErrors.value = `Reading exceeds maximum allowed increase of ${maxIncrease.toLocaleString()} hours. You're trying to increase: ${increase.toLocaleString()} hours`
        }
      }
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0]
      if (firstError) {
        toast.error(firstError, { position: 'top-center' })
      }
      return
    }

    const dto: RunningHourReadingDto = {
      counterId: counter.id!,
      readingDate: formData.readingDate,
      value: Number(formData.value),
      source: 'MANUAL',
    }
    setPendingDto(dto)
    setImpactLoading(true)
    try {
      const impact = await estimateRunningHourReadingImpact(counter.id!, dto)
      setImpactCount(impact?.overdueJobs ?? 0)
    } catch (error: any) {
      console.error('Impact estimate failed:', error)
      setImpactCount(null)
    } finally {
      setImpactLoading(false)
      setConfirmOpen(true)
    }
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '600px',
          maxWidth: '90vw',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>Add Running Hour Reading</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className='flex-grow-1 overflow-auto px-4 py-3'>
            <div className='mb-5'>
              <label className={LABEL}>Counter</label>
              <input
                type='text'
                className='form-control text-dark'
                value={counter.name}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Equipment</label>
              <input
                type='text'
                className='form-control text-dark'
                value={counter.equipmentName || '-'}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>Last Recorded Value</label>
              <input
                type='text'
                className='form-control text-dark'
                value={counter.currentValue.toLocaleString()}
                disabled
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </div>
            <div className='mb-5'>
              <label className={LABEL}>
                Reading Date <span className='text-danger'>*</span>
              </label>
              <input
                type='date'
                className={`form-control text-dark ${errors.readingDate ? 'is-invalid' : ''}`}
                value={formData.readingDate}
                onChange={(e) => {
                  setFormData({ ...formData, readingDate: e.target.value })
                  if (errors.readingDate) setErrors({ ...errors, readingDate: '' })
                }}
                max={todayStr}
              />
              {errors.readingDate && <div className='invalid-feedback'>{errors.readingDate}</div>}
            </div>
            <div className='mb-5'>
              <label className={LABEL}>
                New Reading Value <span className='text-danger'>*</span>
              </label>
              <input
                type='number'
                className={`form-control text-dark ${errors.value ? 'is-invalid' : ''}`}
                value={formData.value}
                onChange={(e) => {
                  setFormData({ ...formData, value: e.target.value })
                  if (errors.value) setErrors({ ...errors, value: '' })
                }}
                min={minVal}
                max={maxVal}
                step='1'
              />
              {errors.value && <div className='invalid-feedback'>{errors.value}</div>}
              <div className={`form-text ${isPastDate ? 'text-primary fw-semibold' : 'text-muted'}`}>
                {helperText}
              </div>
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary btn-sm'
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Add Reading'}
            </button>
          </div>
        </form>
      </div>

      {confirmOpen && pendingDto && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{ background: 'rgba(0,0,0,0.55)', zIndex: 2200 }}
          onClick={(e) => e.target === e.currentTarget && !impactLoading && setConfirmOpen(false)}
        >
          <div
            className='bg-white rounded shadow-lg p-4'
            style={{ width: '520px', maxWidth: '92vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className='mb-3 text-dark'>Confirm Reading Update</h5>
            <div className='text-muted mb-3'>
              This action is <strong>irreversible</strong>. Please confirm the details before proceeding.
            </div>
            <div className='border rounded p-3 mb-3 bg-light'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted'>Counter</span>
                <span className='fw-semibold text-dark'>{counter.name}</span>
              </div>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted'>Reading Date</span>
                <span className='fw-semibold text-dark'>{pendingDto.readingDate}</span>
              </div>
              <div className='d-flex justify-content-between'>
                <span className='text-muted'>New Reading Value</span>
                <span className='fw-semibold text-dark'>{pendingDto.value.toLocaleString()}</span>
              </div>
            </div>
            <div className='mb-3'>
              {impactLoading ? (
                <div className='text-muted'>Checking impact on PMS jobs…</div>
              ) : (
                <div className='text-dark'>
                  Jobs that may become overdue: {' '}
                  <strong>{impactCount == null ? 'Unknown' : impactCount}</strong>
                </div>
              )}
            </div>
            <div className='d-flex justify-content-end gap-2'>
              <button
                type='button'
                className='btn btn-light btn-sm'
                disabled={impactLoading || submitting}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary btn-sm'
                disabled={impactLoading || submitting}
                onClick={async () => {
                  if (!pendingDto) return
                  setSubmitting(true)
                  try {
                    await createRunningHourReading(counter.id!, pendingDto)
                    toast.success('Reading added successfully', { position: 'top-center' })
                    setConfirmOpen(false)
                    onSuccess()
                  } catch (error: any) {
                    const message =
                      error?.response?.data?.message ||
                      error?.response?.data ||
                      error?.message ||
                      'Failed to add reading'
                    toast.error(String(message), { position: 'top-center' })
                  } finally {
                    setSubmitting(false)
                  }
                }}
              >
                Confirm & Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
